import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '../../components/General/Spinner';
import useSubscription from '../../hooks/useSubscription';
import InvitesDataGrid from '../../components/InvitesDataGrid';
import MembersDataGrid from '../../components/MembersDataGrid';
import { subscriptionRoleService } from '../../services/subscriptionRoleService';
import { IInvite } from '../../types/invite';
import { ISubscriptionRoleResult } from '../../types/subscription-role';
import { CreateInviteDialog } from '../../components/Invites/CreateInviteDialog';
import { DeleteInviteDialog } from '../../components/Invites/DeleteInviteDialog';
import { ResendInviteDialog } from '../../components/Invites/ResendInviteDialog';
import { DeleteMemberDialog } from '../../components/Members/DeleteMemberDialog';
import AuditEventsDataGrid from '../../components/AuditEventsDataGrid';
import { ISubscriptionAuditEvent } from '../../types/audit-event';
import { useAlert } from '../../providers/AlertProvider';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import RoleAuthorizeShowGuard from '../../Guards/RoleAuthorizeShowGuard';
import { useAuth } from '../../providers/AuthProvider';
import { CUSSUP_ROLES } from '../../types/auth';

const formatDateToDDMMYYYY = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const SubscriptionDetail: FC = () => {
  const { system, subscriptionId } = useParams();
  const navigate = useNavigate();

  const systemParam = useMemo(() => decodeURI(system as string), [system]);
  const subscriptionIdParam = useMemo(() => decodeURI(subscriptionId as string), [subscriptionId]);

  const { subscription, error, getSubscription } = useSubscription(
    systemParam,
    subscriptionIdParam
  );

  const [invites, setInvites] = useState<IInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState(false);
  const [invitesTotalCount, setInvitesTotalCount] = useState(0);
  const invitesPagination = useCursorPagination(5);
  const [invitesRefreshTrigger, setInvitesRefreshTrigger] = useState(0);
  const [showCreateInviteDialog, setShowCreateInviteDialog] = useState(false);
  const [showDeleteInviteDialog, setShowDeleteInviteDialog] = useState(false);
  const [showResendInviteDialog, setShowResendInviteDialog] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState<{ id: string; email: string } | null>(null);
  const [inviteToResend, setInviteToResend] = useState<{ id: string; email: string } | null>(null);

  const [auditEvents, setAuditEvents] = useState<ISubscriptionAuditEvent[]>([]);
  const [auditEventsLoading, setAuditEventsLoading] = useState(false);
  const [auditEventsError, setAuditEventsError] = useState(false);
  const [auditEventsTotalCount, setAuditEventsTotalCount] = useState(0);
  const auditEventsPagination = useCursorPagination(5);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(null);
  const [autoRefreshMenuAnchor, setAutoRefreshMenuAnchor] = useState<null | HTMLElement>(null);
  const autoRefreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const AUTO_REFRESH_OPTIONS: { label: string; value: number | null }[] = [
    { label: 'Off', value: null },
    { label: '30s', value: 30_000 },
    { label: '1m', value: 60_000 },
    { label: '5m', value: 300_000 },
  ];

  const [members, setMembers] = useState<ISubscriptionRoleResult[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(false);
  const [membersTotalCount, setMembersTotalCount] = useState(0);
  const membersPagination = useCursorPagination(5);
  const [membersRefreshTrigger, setMembersRefreshTrigger] = useState(0);
  const [showDeleteMemberDialog, setShowDeleteMemberDialog] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<{
    identityId: string;
    name: string;
    email: string;
    roleName: string;
  } | null>(null);

  const alert = useAlert();
  const { roles } = useAuth();

  useEffect(() => {
    getSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemParam, subscriptionIdParam]);

  useEffect(() => {
    loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    systemParam,
    subscriptionIdParam,
    invitesPagination.cursor,
    invitesPagination.pageSize,
    invitesRefreshTrigger,
  ]);

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    systemParam,
    subscriptionIdParam,
    membersPagination.cursor,
    membersPagination.pageSize,
    membersRefreshTrigger,
  ]);

  useEffect(() => {
    loadAuditEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    systemParam,
    subscriptionIdParam,
    auditEventsPagination.cursor,
    auditEventsPagination.pageSize,
  ]);

  useEffect(() => {
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
    if (autoRefreshInterval !== null) {
      autoRefreshTimerRef.current = setInterval(() => {
        loadAuditEvents();
      }, autoRefreshInterval);
    }
    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshInterval]);

  const loadInvites = async () => {
    setInvitesLoading(true);
    setInvitesError(false);
    try {
      const invitesData = await subscriptionRoleService.getInvites(
        systemParam,
        subscriptionIdParam,
        invitesPagination.cursor,
        invitesPagination.pageSize,
        'Forward'
      );
      setInvites([...invitesData.items]);
      setInvitesTotalCount(invitesData.totalCount);
    } catch (err) {
      setInvitesError(true);
    } finally {
      setInvitesLoading(false);
    }
  };

  const loadAuditEvents = async () => {
    setAuditEventsLoading(true);
    setAuditEventsError(false);
    try {
      const auditEventsData = await subscriptionRoleService.getAuditEvents(
        systemParam,
        subscriptionIdParam,
        auditEventsPagination.cursor,
        auditEventsPagination.pageSize,
        'Forward'
      );
      setAuditEvents([...auditEventsData.items]);
      setAuditEventsTotalCount(auditEventsData.totalCount);
    } catch (err) {
      setAuditEventsError(true);
    } finally {
      setAuditEventsLoading(false);
    }
  };

  const loadMembers = async () => {
    setMembersLoading(true);
    setMembersError(false);
    try {
      const membersData = await subscriptionRoleService.getMemberRoleAssignments(
        systemParam,
        subscriptionIdParam,
        membersPagination.cursor,
        membersPagination.pageSize,
        'Forward'
      );
      setMembers([...membersData.items]);
      setMembersTotalCount(membersData.totalCount);
    } catch (err) {
      setMembersError(true);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleCreateInvite = async (email: string) => {
    try {
      await subscriptionRoleService.createInvite(systemParam, subscriptionIdParam, email);
      invitesPagination.resetPagination();
      await getSubscription();
      setInvitesRefreshTrigger((prev) => prev + 1);
      alert.setSuccessAlert(`Invite sent successfully to ${email}`);
    } catch (err) {
      alert.setErrorAlert('Failed to send invite. Please try again.');
      throw err;
    }
  };

  const openDeleteInviteDialog = (inviteId: string, inviteEmail: string) => {
    setInviteToDelete({ id: inviteId, email: inviteEmail });
    setShowDeleteInviteDialog(true);
  };

  const handleDeleteInvite = async () => {
    if (!inviteToDelete) return;

    try {
      await subscriptionRoleService.deleteInvite(inviteToDelete.id);
      invitesPagination.resetPagination();
      await getSubscription();
      setInvitesRefreshTrigger((prev) => prev + 1);
      alert.setSuccessAlert(`Invite for ${inviteToDelete.email} has been deleted successfully`);
    } catch (err) {
      alert.setErrorAlert('Failed to delete invite. Please try again.');
      throw err;
    }
  };

  const closeDeleteInviteDialog = () => {
    setShowDeleteInviteDialog(false);
    setInviteToDelete(null);
  };

  const openResendInviteDialog = (inviteId: string, inviteEmail: string) => {
    setInviteToResend({ id: inviteId, email: inviteEmail });
    setShowResendInviteDialog(true);
  };

  const handleResendInvite = async () => {
    if (!inviteToResend) return;

    try {
      // Resend is just creating a new invite with the same email
      await subscriptionRoleService.createInvite(
        systemParam,
        subscriptionIdParam,
        inviteToResend.email
      );
      invitesPagination.resetPagination();
      await getSubscription();
      setInvitesRefreshTrigger((prev) => prev + 1);
      alert.setSuccessAlert(`Invite resent successfully to ${inviteToResend.email}`);
    } catch (err) {
      alert.setErrorAlert('Failed to resend invite. Please try again.');
      throw err;
    }
  };

  const closeResendInviteDialog = () => {
    setShowResendInviteDialog(false);
    setInviteToResend(null);
  };

  const openDeleteMemberDialog = (
    identityId: string,
    memberName: string,
    memberEmail: string,
    roleName: string
  ) => {
    setMemberToDelete({ identityId, name: memberName, email: memberEmail, roleName });
    setShowDeleteMemberDialog(true);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      await subscriptionRoleService.deleteMember(
        systemParam,
        subscriptionIdParam,
        memberToDelete.identityId,
        memberToDelete.roleName
      );
      membersPagination.resetPagination();
      invitesPagination.resetPagination();
      await getSubscription();
      setMembersRefreshTrigger((prev) => prev + 1);
      setInvitesRefreshTrigger((prev) => prev + 1);
      alert.setSuccessAlert(
        `Member ${memberToDelete.name || memberToDelete.email} has been removed successfully`
      );
    } catch (err) {
      alert.setErrorAlert('Failed to remove member. Please try again.');
      throw err;
    }
  };

  const closeDeleteMemberDialog = () => {
    setShowDeleteMemberDialog(false);
    setMemberToDelete(null);
  };

  const viewPreviousPage = () => {
    navigate(-1);
  };

  if (error) {
    return (
      <AuthenticatedTemplate>
        <Stack direction="row" mb={2}>
          <IconButton onClick={viewPreviousPage} sx={{ mr: 0.5 }}>
            <ChevronLeftOutlinedIcon />
          </IconButton>
          <Stack direction="column" flex={1}>
            <Typography variant="MHHeading" color="error">
              Subscription not found
            </Typography>
            <Typography variant="MHCaption">
              The subscription with ID &quot;{subscriptionIdParam}&quot; could not be found. Please
              verify the ID and try again.
            </Typography>
          </Stack>
        </Stack>
      </AuthenticatedTemplate>
    );
  }

  return subscription ? (
    <AuthenticatedTemplate>
      <Stack justifyContent="space-between" mb={1.5} alignItems="center" direction="row">
        <Stack direction="row">
          <IconButton onClick={viewPreviousPage} sx={{ mr: 0.5 }}>
            <ChevronLeftOutlinedIcon />
          </IconButton>
          <Stack direction="column">
            <Typography variant="MHHeading">Specific subscription page</Typography>
            <Typography variant="MHCaption">
              An overview of subscription details and member information.
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={10} md={8}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    General Subscription Information
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '50%' }}>
                    Subscription ID
                  </TableCell>
                  <TableCell data-cy="subscription-id">{subscriptionIdParam}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    System
                  </TableCell>
                  <TableCell data-cy="subscription-system">{systemParam}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Main Brand
                  </TableCell>
                  <TableCell data-cy="subscription-main-brand">{subscription.mainBrand}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Total Seats
                  </TableCell>
                  <TableCell data-cy="subscription-total-seats">{subscription.seats}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Remaining Invites
                  </TableCell>
                  <TableCell data-cy="subscription-remaining-invites">
                    {subscription.remainingInvites}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Main Account ID
                  </TableCell>
                  <TableCell data-cy="subscription-main-account-identity-id">
                    <Box
                      component={Link}
                      to={`/user/detail/${subscription.mainAccountIdentityId}`}
                      sx={{
                        color: 'inherit',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover .link-icon': {
                          opacity: 1,
                        },
                      }}>
                      {subscription.mainAccountIdentityId}
                      <OpenInNewIcon
                        className="link-icon"
                        sx={{
                          fontSize: 16,
                          opacity: 0.6,
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Retention Date
                  </TableCell>
                  <TableCell data-cy="subscription-retention-date">
                    {subscription.retentionEndDate
                      ? formatDateToDDMMYYYY(subscription.retentionEndDate)
                      : '-'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Contract Type
                  </TableCell>
                  <TableCell data-cy="subscription-contract-type">
                    {subscription.contractType ?? '-'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>
                Invites
              </Typography>
              <RoleAuthorizeShowGuard
                requiredRoles={[CUSSUP_ROLES.CUSSUP_SPECIALIZED]}
                currentRoles={roles}>
                <Button
                  variant="brandContained"
                  color="brandBlack"
                  onClick={() => setShowCreateInviteDialog(true)}
                  disabled={subscription.remainingInvites === 0}
                  data-cy="new-invite-button">
                  New invite
                </Button>
              </RoleAuthorizeShowGuard>
            </Stack>
            <InvitesDataGrid
              invites={invites}
              loading={invitesLoading}
              error={invitesError}
              totalCount={invitesTotalCount}
              page={invitesPagination.page}
              pageSize={invitesPagination.pageSize}
              onPageChange={(newPage) => invitesPagination.handlePageChange(newPage, invites)}
              onPageSizeChange={invitesPagination.handlePageSizeChange}
              onDeleteInvite={openDeleteInviteDialog}
              onResendInvite={openResendInviteDialog}
              showDeleteButton={roles.includes(CUSSUP_ROLES.CUSSUP_SPECIALIZED)}
              showResendButton={roles.includes(CUSSUP_ROLES.CUSSUP_SPECIALIZED)}
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="column" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>
              Members
            </Typography>
            <MembersDataGrid
              members={members}
              loading={membersLoading}
              error={membersError}
              totalCount={membersTotalCount}
              page={membersPagination.page}
              pageSize={membersPagination.pageSize}
              onPageChange={(newPage) => membersPagination.handlePageChange(newPage, members)}
              onPageSizeChange={membersPagination.handlePageSizeChange}
              onDeleteMember={openDeleteMemberDialog}
              showDeleteButton={roles.includes(CUSSUP_ROLES.CUSSUP_SPECIALIZED)}
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>
                Audit Events
              </Typography>
              <IconButton
                onClick={loadAuditEvents}
                disabled={auditEventsLoading}
                title="Refresh audit events"
                sx={{ mt: 2 }}>
                <RefreshOutlinedIcon />
              </IconButton>
              <IconButton
                onClick={(e) => setAutoRefreshMenuAnchor(e.currentTarget)}
                title="Auto-refresh options"
                sx={{ mt: 2 }}>
                <MoreVertIcon />
              </IconButton>
              {autoRefreshInterval !== null && (
                <Chip
                  label={`Auto-refresh: ${AUTO_REFRESH_OPTIONS.find((o) => o.value === autoRefreshInterval)?.label}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={() => setAutoRefreshInterval(null)}
                  sx={{ mt: 2 }}
                />
              )}
              <Menu
                anchorEl={autoRefreshMenuAnchor}
                open={Boolean(autoRefreshMenuAnchor)}
                onClose={() => setAutoRefreshMenuAnchor(null)}>
                {AUTO_REFRESH_OPTIONS.map((option) => (
                  <MenuItem
                    key={String(option.value)}
                    onClick={() => {
                      setAutoRefreshInterval(option.value);
                      setAutoRefreshMenuAnchor(null);
                    }}>
                    <ListItemIcon>
                      {autoRefreshInterval === option.value && <CheckIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>{option.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
            <AuditEventsDataGrid
              auditEvents={auditEvents}
              loading={auditEventsLoading}
              error={auditEventsError}
              totalCount={auditEventsTotalCount}
              page={auditEventsPagination.page}
              pageSize={auditEventsPagination.pageSize}
              onPageChange={(newPage) =>
                auditEventsPagination.handlePageChange(newPage, auditEvents)
              }
              onPageSizeChange={auditEventsPagination.handlePageSizeChange}
            />
          </Stack>
        </Grid>
      </Grid>
      <CreateInviteDialog
        open={showCreateInviteDialog}
        onClose={() => setShowCreateInviteDialog(false)}
        onSave={handleCreateInvite}
      />
      <DeleteInviteDialog
        open={showDeleteInviteDialog}
        onClose={closeDeleteInviteDialog}
        onConfirm={handleDeleteInvite}
        inviteEmail={inviteToDelete?.email || ''}
      />
      <ResendInviteDialog
        open={showResendInviteDialog}
        onClose={closeResendInviteDialog}
        onConfirm={handleResendInvite}
        inviteEmail={inviteToResend?.email || ''}
      />
      <DeleteMemberDialog
        open={showDeleteMemberDialog}
        onClose={closeDeleteMemberDialog}
        onConfirm={handleDeleteMember}
        memberName={memberToDelete?.name || ''}
        memberEmail={memberToDelete?.email || ''}
      />
    </AuthenticatedTemplate>
  ) : (
    <Spinner />
  );
};
